<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { useSourceStore } from "@core/stores/source.store"
import { useConfigStore } from "@core/stores/config.store"
import { ALARME_ALLOWED_SOURCE } from "@modules/energy-manager/constants"
import { buildAlarmThresholdVariablesOptions } from "@modules/energy-manager/utils/alarmThresholdVariablesOptions"
import { shouldIncludeAlarmVariableForSource } from "@modules/energy-manager/utils/alarmMonitoringVariables"

const route = useRoute()
const sourceStore = useSourceStore()
const configStore = useConfigStore()
const isAddingThreshold = ref(false)
/** Ouverture de la modale de génération groupée de seuils. */
const isBulkGenerateDialogVisible = ref(false)
const { t } = useI18n({
    messages: {
        fr: {
            alarm: {
                addAlarm: "Ajouter une alarme",
                bulkGenerate: "Générer des alarmes",
                bulkGenerateDisabledTooltip:
                    "Toutes les alarmes possibles pour chaque type de données sont déjà créées. Supprimez-en pour pouvoir en générer de nouveau.",
                emptyAlarmsHint:
                    "Vous n'avez pas encore d'alarme. Utilisez le bouton « Ajouter une alarme » pour en créer une.",
            },
        },
    },
})

const getSourceId = computed(
    () => route.params?._id_source as string | undefined
)
const getSource = computed(() =>
    getSourceId.value ? sourceStore.getSourceById(getSourceId.value) : undefined
)
const getIsNotFetchingSilent = computed(
    () => sourceStore.isFetchingSourceById && !getSource.value?.type
)
const getVariablesOptions = computed(() =>
    buildAlarmThresholdVariablesOptions({
        variables: configStore.getVariables,
        existingProfilKeys: Object.keys(getSource.value?.profil ?? {}),
        allowedTypes: ALARME_ALLOWED_SOURCE,
        shouldIncludeVariableType: (variableType) =>
            shouldIncludeAlarmVariableForSource(variableType, getSource.value),
    })
)

/** True tant qu'aucune variable de profil n'existe : la liste d'alarmes est vide. */
const getHasNoAlarmRows = computed(() => {
    const profil = getSource.value?.profil
    return !profil || Object.keys(profil).length === 0
})

/** Génération groupée possible (données chargées et au moins une variable disponible). */
const getCanOpenBulkGenerate = computed(
    () =>
        !configStore.isLoadingVariables &&
        !getIsNotFetchingSilent.value &&
        getVariablesOptions.value.length > 0
)

/** Chargement en cours : pas de tooltip sur le bouton désactivé. */
const getIsBulkGenerateContextLoading = computed(
    () => configStore.isLoadingVariables || getIsNotFetchingSilent.value
)

/** Tooltip sur « Générer des alarmes » uniquement quand désactivé car aucune variable restante. */
const getBulkGenerateDisabledTooltip = computed(() => {
    if (getIsBulkGenerateContextLoading.value || getCanOpenBulkGenerate.value) {
        return null
    }
    return t("alarm.bulkGenerateDisabledTooltip")
})

watch(
    () => route.params?._id_source,
    async (newId) => {
        if (!newId) return
        await configStore.fetchVariables()
    },
    { immediate: true }
)
</script>

<template>
    <div class="page page-source-alarm flex flex-col gap-4">
        <HeaderPageSource
            title="Alarmes"
            :source-id="getSourceId ?? ''"
            class-header="flex justify-between items-center flex-wrap gap-4"
        >
            <div class="flex flex-wrap items-center gap-2">
                <span
                    class="inline-flex"
                    v-tooltip.top="
                        getBulkGenerateDisabledTooltip ?? { disabled: true }
                    "
                >
                    <BaseButton
                        severity="secondary"
                        icon="pi pi-bolt"
                        icon-pos="right"
                        :disabled="!getCanOpenBulkGenerate"
                        :label="t('alarm.bulkGenerate')"
                        @click="isBulkGenerateDialogVisible = true"
                    />
                </span>
                <BaseButton
                    @click="isAddingThreshold = !isAddingThreshold"
                    icon="pi pi-plus"
                    icon-pos="right"
                    :label="t('alarm.addAlarm')"
                />
            </div>
        </HeaderPageSource>

        <BaseSkeleton
            v-if="configStore.isLoadingVariables || getIsNotFetchingSilent"
            width="100%"
            height="20rem"
        />
        <template v-else-if="getSource && getSourceId">
            <AlarmBulkGenerateDialog
                v-model:visible="isBulkGenerateDialogVisible"
                type="source"
                :item-id="getSourceId"
                :variables-options="getVariablesOptions"
            />
            <AlarmFormAdd
                v-if="isAddingThreshold"
                type="source"
                :item-id="getSourceId"
                :variables-options="getVariablesOptions"
                @cancel="isAddingThreshold = false"
                @save="isAddingThreshold = false"
            />
            <template v-else>
                <BaseMessage v-if="getHasNoAlarmRows" icon="pi pi-info-circle">
                    {{ t("alarm.emptyAlarmsHint") }}
                </BaseMessage>
                <AlarmVariableList
                    v-else
                    type="source"
                    :item-id="getSourceId"
                    :item="getSource"
                />
            </template>
        </template>
    </div>
</template>
